import {useState, useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  Sidebar,
  StatCard,
  Input,
  ViewToggle,
  GridCard,
  ListCard,
  Modal,
  Button,
} from '@/components';
import {useClosetStore, useUserStore} from '@/store';
import {MEMBERS, MEMBER_NAMES, CATEGORY_ICONS, SAMPLE_ITEMS} from '@/data/sample';
import type {ClosetItem, Category} from '@/types';
import obStyles from '@/pages/Onboarding/Onboarding.module.css';
import styles from './ClosetPage.module.css';

const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

export function ClosetPage() {
  const navigate = useNavigate();
  const {syncStatus, syncProgress, syncItemCount, logout} = useUserStore();
  const {
    items,
    setItems,
    activeMemberId,
    activeCategoryName,
    searchQuery,
    sortOption,
    viewMode,
    setActiveMember,
    toggleCategory,
    setSearchQuery,
    setSortOption,
    setViewMode,
    reset: resetCloset,
  } = useClosetStore();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ClosetItem | null>(null);
  const [bannerVisible, setBannerVisible] = useState(syncStatus === 'syncing');

  // Load sample data on mount
  useEffect(() => {
    if (items.length === 0) {
      setItems(SAMPLE_ITEMS);
    }
  }, [items.length, setItems]);

  // Hide banner after sync completes
  useEffect(() => {
    if (syncStatus === 'done' && bannerVisible) {
      const timer = setTimeout(() => setBannerVisible(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [syncStatus, bannerVisible]);

  // Filter and sort items
  const filtered = useMemo(() => {
    let result = [...items];

    if (activeMemberId !== 'all') {
      result = result.filter((i) => i.member === activeMemberId);
    }
    if (activeCategoryName) {
      result = result.filter((i) => i.category === activeCategoryName);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (i) =>
          i.brand.toLowerCase().includes(q) ||
          i.name.toLowerCase().includes(q) ||
          i.category.toLowerCase().includes(q) ||
          i.subcategory.toLowerCase().includes(q) ||
          i.color.toLowerCase().includes(q),
      );
    }

    switch (sortOption) {
      case 'oldest':
        result.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'brand-az':
        result.sort((a, b) => a.brand.localeCompare(b.brand));
        break;
      default:
        result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }

    return result;
  }, [items, activeMemberId, activeCategoryName, searchQuery, sortOption]);

  // Build categories from all items
  const categories: Category[] = useMemo(() => {
    const counts = items.reduce<Record<string, number>>((acc, item) => {
      acc[item.category] = (acc[item.category] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, count]) => ({
        name,
        icon: CATEGORY_ICONS[name] || '📦',
        count,
      }));
  }, [items]);

  // Stats
  const totalValue = filtered.reduce((sum, i) => sum + i.price, 0);
  const uniqueBrands = new Set(filtered.map((i) => i.brand)).size;
  const uniqueCategories = new Set(filtered.map((i) => i.category)).size;
  const showMemberBadge = activeMemberId === 'all';

  function handleItemClick(item: ClosetItem) {
    setSelectedItem(item);
    setModalOpen(true);
  }

  function handleLogout() {
    logout();
    resetCloset();
    navigate('/onboarding');
  }

  return (
    <div className={styles.app}>
      <Sidebar
        members={MEMBERS}
        categories={categories}
        activeMemberId={activeMemberId}
        activeCategoryName={activeCategoryName}
        onMemberSelect={setActiveMember}
        onCategorySelect={toggleCategory}
        onAddMember={() => {}}
        onShare={() => {}}
        onLogout={handleLogout}
      />

      <div className={styles.main}>
        {/* Sync banner */}
        {bannerVisible && (
          <div className={obStyles.syncBanner}>
            <div
              className={[
                obStyles.syncBannerRing,
                syncStatus === 'done' ? obStyles.syncBannerRingDone : '',
              ]
                .filter(Boolean)
                .join(' ')}
            />
            <div className={obStyles.syncBannerText}>
              Syncing... <strong>{syncItemCount}</strong> items found
            </div>
            <div className={obStyles.syncBannerBar}>
              <div className={obStyles.syncBannerFill} style={{width: `${syncProgress}%`}} />
            </div>
            <div className={obStyles.syncBannerPct}>{syncProgress}%</div>
          </div>
        )}

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            {activeMemberId === 'all'
              ? 'All'
              : MEMBER_NAMES[activeMemberId] || activeMemberId}
          </div>
        </div>

        {/* Stats */}
        <div className={styles.statsWrap}>
          <div className={styles.statsGrid}>
            <StatCard value={String(filtered.length)} label="Items" />
            <StatCard
              value={'$' + totalValue.toLocaleString('en-US', {maximumFractionDigits: 0})}
              label="Total Value"
            />
            <StatCard value={String(uniqueBrands)} label="Brands" />
            <StatCard value={String(uniqueCategories)} label="Categories" />
          </div>
        </div>

        {/* Toolbar */}
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Input
              icon={<SearchIcon />}
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className={styles.sortSelect}
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value as typeof sortOption)}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="price-desc">Price: High → Low</option>
            <option value="price-asc">Price: Low → High</option>
            <option value="brand-az">Brand: A–Z</option>
          </select>
          <ViewToggle mode={viewMode} onChange={setViewMode} />
        </div>

        {/* Content */}
        <div className={styles.content}>
          {filtered.length === 0 ? (
            <div className={styles.empty}>
              <h3>No items found</h3>
              <p>Try adjusting your search or filters.</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className={styles.gridView}>
              {filtered.map((item) => (
                <GridCard
                  key={item.id}
                  item={item}
                  showMemberBadge={showMemberBadge}
                  memberName={MEMBER_NAMES[item.member]}
                  onClick={() => handleItemClick(item)}
                />
              ))}
            </div>
          ) : (
            <div className={styles.listView}>
              {filtered.map((item) => (
                <ListCard
                  key={item.id}
                  item={item}
                  showMember={showMemberBadge}
                  memberName={MEMBER_NAMES[item.member]}
                  onClick={() => handleItemClick(item)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Item Detail Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedItem?.brand ?? ''}
      >
        {selectedItem && (
          <>
            <div className={styles.modalImage}>
              <span style={{fontSize: 80}}>{selectedItem.emoji}</span>
            </div>
            <div className={styles.modalGrid}>
              <DetailField label="Product" value={selectedItem.name} />
              <DetailField label="Brand" value={selectedItem.brand} />
              <DetailField
                label="Category"
                value={`${selectedItem.category} / ${selectedItem.subcategory}`}
              />
              <DetailField label="Price" value={`$${selectedItem.price}`} />
              <DetailField label="Color" value={selectedItem.color} />
              <DetailField label="Size" value={selectedItem.size} />
              <DetailField label="Purchased" value={formatDate(selectedItem.date)} />
              <DetailField
                label="Family"
                value={MEMBER_NAMES[selectedItem.member] || selectedItem.member}
              />
            </div>
            <div className={styles.modalActions}>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>
                Close
              </Button>
              <Button>Reorder</Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

function DetailField({label, value}: {label: string; value: string}) {
  return (
    <div className={styles.detailField}>
      <label className={styles.detailLabel}>{label}</label>
      <span className={styles.detailValue}>{value}</span>
    </div>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
